// ──────────────────────────────────────────────
// STRANGELOVE OS — Design Tokens
// Single source of truth for all design values.
// Referenced by Tailwind CSS variables in index.css.
// ──────────────────────────────────────────────

export const colors = {
  // Core backgrounds — War Room dark greens
  bgPrimary: "#010D0C",       // War Room base darkness
  bgElevated: "#0A1A19",      // Sidebar, panels
  bgSurface: "#111B1A",       // Bento cards, inputs

  // Accents — bright colors, used sparingly
  electricBlue: "#4B75F2",    // Primary actions, nav active, Big Board glow
  mint: "#44F2D2",            // Success, Strangelove AI text, radar green
  electricYellow: "#EBF216",  // Warnings, Watch List, scan-in-progress

  // Text — black & white palette
  textPrimary: "#E8E8E8",     // Off-White — all readable text
  textSecondary: "#777777",   // Neutral gray — labels, timestamps
  textDisabled: "#555555",    // Dim gray — disabled, phased-out

  // Borders — neutral, subtle
  border: "rgba(255, 255, 255, 0.08)",  // Subtle white edge

  // Score tiers
  tierStrikeNow: "#F24B4B",   // 90-100 — Signal Red
  tierWinner: "#4B75F2",      // 80-89 — Electric Blue
  tierWatchList: "#EBF216",   // 70-79 — Electric Yellow
  tierParkingLot: "#555555",  // 60-69 — Dim Gray

  // Doomsday gauge
  doomDanger: "#CC2936",      // Far below target
  doomCaution: "#EBF216",     // Midpoint
  doomOnTrack: "#44F2D2",     // Approaching/exceeding target

  // Semantic
  error: "#E53E3E",           // Error states, destructive
  success: "#44F2D2",         // Same as mint
  warning: "#EBF216",         // Same as electricYellow
} as const;

export const fonts = {
  brand: "'Bebas Neue', sans-serif",        // Headers, titles, tier names
  mono: "'JetBrains Mono', monospace",      // Scores, data values, AI voice
  body: "'Inter', sans-serif",              // Body text, labels, forms
} as const;

export const borderRadius = {
  card: "10px",
  button: "6px",
  badge: "4px",
} as const;

// Doomsday Machine
export const doomsday = {
  monthlyTarget: 83333,
  gradientStops: [colors.doomDanger, colors.doomCaution, colors.doomOnTrack],
} as const;

// Score tier definitions
export const scoreTiers = [
  { name: "STRIKE NOW", min: 90, max: 100, color: colors.tierStrikeNow, pulse: true },
  { name: "WAR ROOM WINNER", min: 80, max: 89, color: colors.tierWinner, pulse: false },
  { name: "WATCH LIST", min: 70, max: 79, color: colors.tierWatchList, pulse: false },
  { name: "PARKING LOT", min: 60, max: 69, color: colors.tierParkingLot, pulse: false },
  { name: "PASS", min: 0, max: 59, color: "#3A4A48", pulse: false },
] as const;

export function getScoreTier(score: number) {
  return scoreTiers.find((t) => score >= t.min && score <= t.max) ?? null;
}

// Saturation levels
export const saturationColors = {
  blue_ocean: { color: "#44F2D2", bg: "rgba(68, 242, 210, 0.12)", label: "BLUE OCEAN" },
  cracked_door: { color: "#4B75F2", bg: "rgba(75, 117, 242, 0.12)", label: "CRACKED DOOR" },
  red_ocean: { color: "#F24B4B", bg: "rgba(242, 75, 75, 0.12)", label: "RED OCEAN" },
} as const;

export type SaturationLevel = keyof typeof saturationColors;

// Strangelove quotes for loading states (Appendix A)
export const strangeloveQuotes = [
  "The whole point of the Doomsday Machine is lost if you keep it a secret!",
  "Mein Fuhrer, I can walk!",
  "Gentlemen, you can't fight in here! This is the War Room!",
  "He'll see everything. He'll see The Big Board!",
  "Of course, the whole point of a Doomsday Machine is lost, if you keep it a secret!",
  "It is not only possible, it is essential.",
  "I can no longer sit back and allow Communist infiltration.",
  "Regrettably, yes. But it is, you know, a sacrifice required for the future of the human race.",
] as const;
